import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState
} from 'react';
import { TaskEntity } from '../../secure-portal/tasks/Task.interface';

export interface CartContextProps {
  cart: TaskEntity[];
  processedEntities: TaskEntity[];
  setProcessedEntities: Dispatch<SetStateAction<TaskEntity[]>>;
  addToCart: (taskEntity: TaskEntity) => void;
  removeFromCart: (taskEntity: TaskEntity) => void;
  clearCart: () => void;
}

export const CartContext = createContext<CartContextProps>(null!);

export const useCartContext = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error(
      'useCartContext must be used within the context of the CartProvider component'
    );
  }
  return context;
};

export function getCartIndex(cart: TaskEntity[], taskEntity: TaskEntity) {
  // make sure the cart item is the same task entity
  // as ids can match for different task entities
  // e.g. user need id 1 can match with design output id 1
  return cart.findIndex(item => {
    return (
      item.id === taskEntity.id &&
      item.task &&
      taskEntity.task &&
      item.task.id === taskEntity.task.id
    );
  });
}

export const CartProvider: React.FC = ({ children }) => {
  const [cart, setCart] = useState<TaskEntity[]>([]);
  const [processedEntities, setProcessedEntities] = useState<TaskEntity[]>([]);

  const addToCart = useCallback((taskEntity: TaskEntity) => {
    setCart(cart => {
      // don't insert duplicates
      return getCartIndex(cart, taskEntity) > -1
        ? cart
        : cart.concat(taskEntity);
    });
  }, []);

  const removeFromCart = useCallback((taskEntity: TaskEntity) => {
    setCart(cart => {
      const index = getCartIndex(cart, taskEntity);
      if (index > -1) {
        cart = Array.from(cart);
        cart.splice(index, 1);
      }
      return cart;
    });
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  return (
    <CartContext.Provider
      value={{
        cart,
        processedEntities,
        setProcessedEntities,
        addToCart,
        removeFromCart,
        clearCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
